import React from 'react';
import { RCFavoriteCard } from '../internalComponents';
import type { LinkElementProps } from '../internalComponents/RCFavoriteCard/RCFavoriteCard';
import { RCStarRating, RCAvatar } from '../';
import DefaultShopAvatar from '../../images/avatars/shop-brand-default.png';

export interface Shop {
  /** The name of the shop on the card */
  name: string;

  /** Shop location, e.g. Minneapolis MN, United States */
  location: string;

  /** Shop rating, value should be between 0-1 */
  rating: number;

  /** The number of ratings that shop has */
  ratingCount?: number;

  /** The URL of the shop's thumbnail. If empty this will fall back to a default shop avatar. */
  thumbnailURL?: string;
}

interface RCSavedShopCardProps {
  shop: Shop;

  /** Sits adjacent to the watchButtonElement inside the card and should be the same size/shape. Designed for `<RCIconButton>` */
  secondaryAction?: React.ReactElement;

  /** The main element that renders a watch/favorite button. Designed to support `<RCFavoriteButton>` */
  watchButtonElement?: React.ReactElement;

  /** The link element (e.g. CoreLink)
   * Example: `linkElement={(children) => (<CoreLink to="/whatever/url/">{children}</CoreLink>)}`
   * Including this makes the whole card clickable
  */
  linkElement: (children: React.ReactNode) => React.ReactElement<LinkElementProps>;

  /** Enabling this will allow the card to grow in height to fill its parent. Helpful for when inside a grid.
   * Disable when only showing on card in a given UI */
  verticalStretch?: boolean;
}

export function RCSavedShopCard({
  shop,
  secondaryAction = undefined,
  watchButtonElement = undefined,
  verticalStretch = true,
  linkElement,
}: RCSavedShopCardProps) {
  return (
    <RCFavoriteCard
      type="shop"
      title={shop.name}
      subtitle={shop.location}
      primaryAction={watchButtonElement}
      secondaryAction={secondaryAction}
      linkElement={linkElement}
      verticalStretch={verticalStretch}
      additionalContent={
        <RCStarRating
          rating={shop.rating}
          count={shop.ratingCount}
          hideTooltip
        />
      }
      avatar={
        <RCAvatar
          userName={shop.name}
          thumbnailURL={shop.thumbnailURL || DefaultShopAvatar}
          thumbnailFallback="initial"
        />
      }
    />
  );
}
